(() => {
  const initialized = new WeakSet();
  const controllers = new WeakMap();

  class CorsailleCollectionController {
    constructor(root) {
      this.root = root;
      this.root.dataset.csCollectionReady = 'true';
      this.sectionId = root.dataset.sectionId || '';
      this.family = root.dataset.family || '';
      this.collectionHandle = root.dataset.collectionHandle || '';
      this.defaultSort = root.dataset.defaultSort || '';
      this.recommendedSortLabel = root.dataset.sortRecommendedLabel || 'Recomandarea Corsaille';
      this.handleClick = this.handleClick.bind(this);
      this.handleChange = this.handleChange.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleHistoryState = this.handleHistoryState.bind(this);
      this.handleHeroVideoState = this.handleHeroVideoState.bind(this);
      this.handleHeroVideoLoadedMetadata = this.handleHeroVideoLoadedMetadata.bind(this);
      this.handleHeroVideoPolicyChange = this.handleHeroVideoPolicyChange.bind(this);
      this.syncDiscoveryOverflow = this.syncDiscoveryOverflow.bind(this);
      this.syncFeatureStageCadence = this.syncFeatureStageCadence.bind(this);
      this.saveScroll = this.saveScroll.bind(this);
      this.observeProducts = this.observeProducts.bind(this);
      this.unobserveProducts = this.unobserveProducts.bind(this);
      this.impressionTimers = new Map();
      this.measuredImpressions = new WeakSet();

      root.addEventListener('click', this.handleClick);
      root.addEventListener('change', this.handleChange);
      root.addEventListener('keydown', this.handleKeydown);
      window.addEventListener('pagehide', this.saveScroll);
      window.addEventListener('popstate', this.handleHistoryState);
      window.addEventListener('pageshow', this.handleHistoryState);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);

      this.setupHeroVideo();
      this.setupDiscoveryRail();
      this.setupFeatureStageCadence();

      this.impressionObserver = 'IntersectionObserver' in window
        ? new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting || entry.intersectionRatio < 0.5 || document.visibilityState !== 'visible') {
                this.cancelImpression(entry.target);
                return;
              }
              if (this.impressionTimers.has(entry.target)) return;
              const timer = window.setTimeout(() => {
                const pending = this.impressionTimers.get(entry.target);
                this.impressionTimers.delete(entry.target);
                if (!pending || pending.ratio < 0.5 || document.visibilityState !== 'visible' || !entry.target.isConnected) return;
                const card = entry.target.querySelector('[data-cs-product-card]');
                if (!card) return;
                this.measuredImpressions.add(entry.target);
                this.emit('product_impression', {
                  productId: card.dataset.csProductId || card.dataset.productId,
                  productTitle: card.dataset.csProductTitle || card.dataset.productTitle || card.querySelector('.cs-product-card__title')?.textContent?.trim(),
                  productHandle: card.dataset.csProductHandle || card.dataset.productHandle,
                  variantId: card.dataset.csVariantId || card.dataset.variantId,
                  position: Number(entry.target.dataset.csProductPosition || card.dataset.csPosition || card.dataset.position || 0),
                  mediaType: card.dataset.csMediaType || '',
                });
                this.impressionObserver.unobserve(entry.target);
              }, 500);
              this.impressionTimers.set(entry.target, { timer, ratio: entry.intersectionRatio });
            });
          }, { threshold: [0, 0.5] })
        : null;

      this.mutationObserver = new MutationObserver((records) => {
        records.forEach((record) => {
          record.addedNodes.forEach((node) => {
            if (node instanceof Element) this.observeProducts(node);
          });
          record.removedNodes.forEach((node) => {
            if (!(node instanceof Element)) return;
            this.unobserveProducts(node);
            if (!node.isConnected) {
              node.matches?.('[data-cwl-stage], [data-cwl-hero]') && node.cwlController?.destroy?.();
              window.CorsailleLivingWater?.destroyScope?.(node);
            }
          });
        });
        this.normalizeInterface();
        this.syncFeatureStageCadence();
        window.CorsailleLivingWater?.initialize?.(this.root);
      });
      this.mutationObserver.observe(root, { childList: true, subtree: true });

      this.observeProducts(root);
      this.normalizeInterface();
      this.restoreScroll();
      this.emit('collection_view', {
        result_count: Number(root.dataset.resultCount || 0),
        total_count: Number(root.dataset.totalCount || 0),
      });
    }

    get storageKey() {
      return `cs:collection-scroll:${window.location.pathname}${window.location.search}`;
    }

    emit(event, extra = {}) {
      window.CorsailleAnalytics?.emit(event, {
        category: this.family,
        object_type: this.family,
        collection: this.collectionHandle,
        source: 'collection',
        ...extra,
      });
    }

    normalizeInterface() {
      this.root.querySelectorAll('select[name="sort_by"]').forEach((select) => {
        const recommended = Array.from(select.options).find((option) => option.value === this.defaultSort);
        if (recommended && recommended.textContent !== this.recommendedSortLabel) {
          recommended.textContent = this.recommendedSortLabel;
        }
      });

      this.root.querySelectorAll('.facet-filters__label label').forEach((label) => {
        if (label.textContent !== 'SORTARE') label.textContent = 'SORTARE';
      });
      this.root.querySelectorAll('.mobile-facets__open-label.medium-hide.large-up-hide').forEach((label) => {
        if (label.textContent !== 'RAFINEAZĂ') label.textContent = 'RAFINEAZĂ';
      });
      this.root.querySelectorAll('.mobile-facets__open-label.small-hide').forEach((label) => {
        if (label.textContent !== 'FILTRE') label.textContent = 'FILTRE';
      });

      this.root.querySelectorAll('[data-cs-observatory]').forEach((module) => {
        const tabs = Array.from(module.querySelectorAll('[data-cs-observatory-tab]'));
        const selectedIndex = Math.max(0, tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true'));
        tabs.forEach((tab, index) => {
          tab.classList.toggle('is-active', index === selectedIndex);
          tab.setAttribute('aria-selected', String(index === selectedIndex));
          tab.tabIndex = index === selectedIndex ? 0 : -1;
        });
      });

      this.syncConstraintInterface();
    }

    setupHeroVideo() {
      this.heroVideo = this.root.querySelector('[data-cs-collection-hero-video]');
      this.heroVideoControl = this.root.querySelector('[data-cs-hero-video-control]');
      if (!this.heroVideo) return;
      this.heroVideoBlocked = false;
      this.heroVideoPlayPending = false;
      this.heroVideoControl?.remove();
      this.heroVideoControl = null;
      const motionDescription = this.root.querySelector('[id^="CsCollectionHeroMotionDescription-"]');
      if (motionDescription) motionDescription.textContent = 'Vizualizare editorială 360 de grade, redată continuu.';
      this.heroVideo.muted = this.heroVideo.defaultMuted = true;
      this.heroVideo.autoplay = this.heroVideo.loop = this.heroVideo.playsInline = true;
      this.heroVideo.controls = false;
      this.heroVideo.removeAttribute('controls');
      this.heroVideo.setAttribute('autoplay', '');

      this.heroVideo.addEventListener('play', this.handleHeroVideoState);
      this.heroVideo.addEventListener('pause', this.handleHeroVideoState);
      this.heroVideo.addEventListener('ended', this.handleHeroVideoState);
      this.heroVideo.addEventListener('loadedmetadata', this.handleHeroVideoLoadedMetadata);
      this.heroVideo.addEventListener('canplay', this.handleHeroVideoPolicyChange);
      this.handleHeroVideoLoadedMetadata();

      this.heroMotionPreference = window.matchMedia?.('(prefers-reduced-motion: reduce)') || null;
      this.heroMotionPreference?.addEventListener?.('change', this.handleHeroVideoPolicyChange);
      navigator.connection?.addEventListener?.('change', this.handleHeroVideoPolicyChange);

      this.heroVideoInView = !('IntersectionObserver' in window);
      if ('IntersectionObserver' in window) {
        this.heroVideoObserver = new IntersectionObserver((entries) => {
          const entry = entries[0];
          this.heroVideoInView = Boolean(entry?.isIntersecting && entry.intersectionRatio >= 0.15);
          this.syncHeroVideoPlayback();
        }, { threshold: [0, 0.15, 0.55], rootMargin: '100px 0px' });
        this.heroVideoObserver.observe(this.heroVideo);
      }

      this.handleHeroVideoPolicyChange();
    }

    handleHeroVideoLoadedMetadata() {
      if (!this.heroVideo) return;
      const configuredRate = Number(this.heroVideo.dataset.csHeroPlaybackRate);
      const basePlaybackRate = Number.isFinite(configuredRate) && configuredRate > 0
        ? Math.max(0.35, Math.min(1.5, configuredRate))
        : 1;
      const playbackRate = basePlaybackRate * 2;
      this.heroVideo.loop = true;
      this.heroVideo.defaultPlaybackRate = playbackRate;
      this.heroVideo.playbackRate = playbackRate;
      this.root.dataset.csHeroVideoPlaybackRate = String(playbackRate);
    }

    handleHeroVideoPolicyChange() {
      if (!this.heroVideo) return;
      this.heroVideoPolicyPaused = false;
      this.root.dataset.csHeroVideoPolicy = this.heroVideoBlocked ? 'browser-blocked' : 'autoplay';
      this.syncHeroVideoPlayback();
    }

    syncHeroVideoPlayback() {
      if (!this.heroVideo) return;
      const shouldPlay = this.heroVideoInView
        && document.visibilityState === 'visible';

      if (!shouldPlay) {
        if (!this.heroVideo.paused) this.heroVideo.pause();
        this.syncHeroVideoControl();
        return;
      }

      this.heroVideo.muted = true;
      this.heroVideo.defaultMuted = true;
      if (this.heroVideoBlocked || this.heroVideoPlayPending || !this.heroVideo.paused) return;
      this.heroVideoPlayPending = true;
      let playback;
      try { playback = this.heroVideo.play(); }
      catch { this.heroVideoBlocked = true; this.heroVideoPlayPending = false; this.root.dataset.csHeroVideoPolicy = 'browser-blocked'; return; }
      Promise.resolve(playback).catch(error => {
        if (error?.name !== 'AbortError') {
          this.heroVideoBlocked = true;
          this.root.dataset.csHeroVideoPolicy = 'browser-blocked';
          this.syncHeroVideoControl();
        }
      }).finally(() => { this.heroVideoPlayPending = false; });
    }

    handleHeroVideoState(event) {
      if (event?.type === 'ended' && this.heroVideo && !this.heroVideoPolicyPaused) {
        this.heroVideo.currentTime = 0;
        this.syncHeroVideoPlayback();
      }
      this.syncHeroVideoControl();
    }

    syncHeroVideoControl() {
      if (!this.heroVideo) return;
      const playing = !this.heroVideo.paused && !this.heroVideo.ended;
      this.root.dataset.csHeroVideoState = playing ? 'playing' : 'paused';
      if (!this.heroVideoControl) return;
      const label = this.heroVideoControl.querySelector('[data-cs-hero-video-control-label]');
      const icon = this.heroVideoControl.querySelector('[data-cs-hero-video-icon]');
      this.heroVideoControl.dataset.state = playing ? 'playing' : 'paused';
      this.heroVideoControl.setAttribute('aria-label', playing ? 'Pune pe pauză animația 360 de grade' : 'Redă animația 360 de grade');
      if (label) label.textContent = playing ? 'PAUZĂ' : 'REDĂ';
      if (icon) icon.textContent = playing ? 'Ⅱ' : '▶';
    }

    setupDiscoveryRail() {
      this.discovery = this.root.querySelector('.cs-collection__discovery');
      this.discoveryList = this.discovery?.querySelector('.cs-collection__discovery-list');
      if (!this.discovery || !this.discoveryList) return;
      this.discoveryList.addEventListener('scroll', this.syncDiscoveryOverflow, { passive: true });
      window.addEventListener('resize', this.syncDiscoveryOverflow);
      requestAnimationFrame(this.syncDiscoveryOverflow);

      const active = this.discoveryList.querySelector('.cs-collection__discovery-link.is-active');
      if (active) {
        const listRect = this.discoveryList.getBoundingClientRect();
        const activeRect = active.getBoundingClientRect();
        const activeOutsideRail = activeRect.left < listRect.left || activeRect.right > listRect.right;
        if (activeOutsideRail) {
          const maximum = Math.max(0, this.discoveryList.scrollWidth - this.discoveryList.clientWidth);
          const activeLeftInRail = activeRect.left - listRect.left + this.discoveryList.scrollLeft;
          const centeredLeft = activeLeftInRail - ((this.discoveryList.clientWidth - activeRect.width) / 2);
          const left = Math.max(0, Math.min(maximum, centeredLeft));
          this.discoveryList.scrollTo({ left, behavior: 'auto' });
          requestAnimationFrame(this.syncDiscoveryOverflow);
        }
      }
    }

    syncDiscoveryOverflow() {
      if (!this.discovery || !this.discoveryList) return;
      const maximum = Math.max(0, this.discoveryList.scrollWidth - this.discoveryList.clientWidth);
      const position = Math.max(0, this.discoveryList.scrollLeft);
      const hasOverflow = maximum > 2;
      this.discoveryList.tabIndex = hasOverflow ? 0 : -1;
      const canGoBack = hasOverflow && position > 2;
      const canGoForward = hasOverflow && position < maximum - 2;
      this.discovery.dataset.csDiscoveryOverflow = canGoForward ? 'more' : (hasOverflow ? 'end' : 'none');
      const previous = this.discovery.querySelector('[data-cs-discovery-previous]');
      const next = this.discovery.querySelector('[data-cs-discovery-next]');
      if (previous) {
        previous.hidden = !hasOverflow;
        previous.disabled = !canGoBack;
        previous.setAttribute('aria-disabled', String(!canGoBack));
      }
      if (next) {
        next.hidden = !hasOverflow;
        next.disabled = !canGoForward;
        next.setAttribute('aria-disabled', String(!canGoForward));
      }
    }

    setupFeatureStageCadence() {
      this.featureStageMedia = window.matchMedia('(min-width: 1200px)');
      this.featureStageMedia.addEventListener?.('change', this.syncFeatureStageCadence);
      this.syncFeatureStageCadence();
    }

    syncFeatureStageCadence() {
      const stages = Array.from(this.root.querySelectorAll('[data-cs-component="feature-stage"][data-cs-position]'));
      stages.forEach((stage) => {
        const configuredPosition = Number(stage.dataset.csPosition || 0);
        if (!configuredPosition) return;
        const targetPosition = this.featureStageMedia?.matches
          ? Math.ceil(configuredPosition / 4) * 4
          : configuredPosition;
        const target = this.root.querySelector(`[data-cs-product-position="${targetPosition}"]`);
        if (!target || target.nextElementSibling === stage) return;
        target.insertAdjacentElement('afterend', stage);
      });
    }

    syncConstraintInterface() {
      if (window.CorsailleCollectionTagFilters) {
        window.CorsailleCollectionTagFilters.sync(this.root);
        return;
      }
      const links = Array.from(this.root.querySelectorAll('[data-cs-constraint-tag]'));
      const verified = new Map(links.map((link) => [link.dataset.csConstraintTag || '', link.dataset.csConstraintLabel || '']));
      const clearLinks = Array.from(this.root.querySelectorAll('[data-cs-constraint-clear]'));
      const clearHref = clearLinks[0]?.getAttribute('href') || window.location.pathname;
      const activeLink = links.find((link) => link.classList.contains('is-active') || link.getAttribute('aria-current') === 'page');
      const requested = activeLink?.dataset.csConstraintTag || '';
      const active = verified.has(requested) ? requested : '';

      links.forEach((link) => {
        const tag = link.dataset.csConstraintTag || '';
        const selected = Boolean(active && tag === active);
        if (!link.dataset.csConstraintHref) link.dataset.csConstraintHref = link.getAttribute('href') || '';
        link.classList.toggle('is-active', selected);
        if (selected) link.setAttribute('aria-current', 'page');
        else link.removeAttribute('aria-current');
        link.href = selected ? clearHref : link.dataset.csConstraintHref;
      });

      clearLinks.forEach((link) => {
        link.classList.toggle('is-active', !active);
        if (active) link.removeAttribute('aria-current');
        else link.setAttribute('aria-current', 'page');
      });

      this.root.querySelectorAll('#main-collection-filters form').forEach((form) => {
        form.querySelector('input[data-cs-constraint-field]')?.remove();
      });

      const tokenRegion = this.root.querySelector('[data-cs-filter-tokens]');
      const constraintToken = tokenRegion?.querySelector('[data-cs-constraint-token]');
      const constraintTokenLabel = constraintToken?.querySelector('[data-cs-constraint-token-label]');
      const constraintTokenRemoveLabel = constraintToken?.querySelector('[data-cs-constraint-token-remove-label]');
      const nativeTokens = tokenRegion?.querySelectorAll('[data-cs-native-filter-token]').length || 0;
      if (constraintToken) {
        constraintToken.hidden = !active;
        constraintToken.href = clearHref;
      }
      const visibleTokenLabel = verified.get(active) || '';
      const removeTokenLabel = active ? `Elimină selecția ${visibleTokenLabel}` : '';
      if (constraintTokenLabel && constraintTokenLabel.textContent !== visibleTokenLabel) constraintTokenLabel.textContent = visibleTokenLabel;
      if (constraintTokenRemoveLabel && constraintTokenRemoveLabel.textContent !== removeTokenLabel) constraintTokenRemoveLabel.textContent = removeTokenLabel;
      if (tokenRegion) tokenRegion.hidden = !active && nativeTokens === 0;
      const clearAll = tokenRegion?.querySelector('[data-cs-clear-all]');
      if (clearAll) clearAll.hidden = !active && nativeTokens === 0;
    }

    saveScroll() {
      try {
        sessionStorage.setItem(
          this.storageKey,
          JSON.stringify({ x: window.scrollX, y: window.scrollY, savedAt: Date.now() }),
        );
      } catch (_error) {
        // Storage is progressive enhancement; native history remains available.
      }
    }

    restoreScroll() {
      try {
        const saved = JSON.parse(sessionStorage.getItem(this.storageKey) || 'null');
        if (!saved || Date.now() - saved.savedAt > 30 * 60 * 1000 || window.scrollY > 1) return;
        requestAnimationFrame(() => window.scrollTo(saved.x || 0, saved.y || 0));
      } catch (_error) {
        // Ignore unavailable or malformed storage.
      }
    }

    observeProducts(scope) {
      if (!this.impressionObserver) return;
      if (scope.matches?.('[data-cs-product-position]') && !this.measuredImpressions.has(scope)) this.impressionObserver.observe(scope);
      scope.querySelectorAll?.('[data-cs-product-position]').forEach((item) => {
        if (!this.measuredImpressions.has(item)) this.impressionObserver.observe(item);
      });
    }

    cancelImpression(item) {
      const pending = this.impressionTimers.get(item);
      if (!pending) return;
      window.clearTimeout(pending.timer);
      this.impressionTimers.delete(item);
    }

    unobserveProducts(scope) {
      if (!this.impressionObserver) return;
      const items = [];
      if (scope.matches?.('[data-cs-product-position]')) items.push(scope);
      scope.querySelectorAll?.('[data-cs-product-position]').forEach((item) => items.push(item));
      items.forEach((item) => {
        this.cancelImpression(item);
        this.impressionObserver.unobserve(item);
      });
    }

    handleVisibilityChange() {
      this.syncHeroVideoPlayback();
      if (!this.impressionObserver) return;
      if (document.visibilityState !== 'visible') {
        this.unobserveProducts(this.root);
        return;
      }
      this.observeProducts(this.root);
    }

    handleHistoryState() {
      this.syncConstraintInterface();
      this.syncHeroVideoPlayback();
    }

    destroy() {
      this.root.removeEventListener('click', this.handleClick);
      this.root.removeEventListener('change', this.handleChange);
      this.root.removeEventListener('keydown', this.handleKeydown);
      window.removeEventListener('pagehide', this.saveScroll);
      window.removeEventListener('popstate', this.handleHistoryState);
      window.removeEventListener('pageshow', this.handleHistoryState);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      this.heroVideo?.removeEventListener('play', this.handleHeroVideoState);
      this.heroVideo?.removeEventListener('pause', this.handleHeroVideoState);
      this.heroVideo?.removeEventListener('ended', this.handleHeroVideoState);
      this.heroVideo?.removeEventListener('loadedmetadata', this.handleHeroVideoLoadedMetadata);
      this.heroVideo?.removeEventListener('canplay', this.handleHeroVideoPolicyChange);
      this.heroMotionPreference?.removeEventListener?.('change', this.handleHeroVideoPolicyChange);
      navigator.connection?.removeEventListener?.('change', this.handleHeroVideoPolicyChange);
      this.heroVideoObserver?.disconnect();
      this.discoveryList?.removeEventListener('scroll', this.syncDiscoveryOverflow);
      window.removeEventListener('resize', this.syncDiscoveryOverflow);
      this.featureStageMedia?.removeEventListener?.('change', this.syncFeatureStageCadence);
      this.impressionTimers.forEach(({ timer }) => window.clearTimeout(timer));
      this.impressionTimers.clear();
      this.impressionObserver?.disconnect();
      this.mutationObserver?.disconnect();
    }

    handleChange(event) {
      const field = event.target;
      if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement)) return;

      if (field.name === 'sort_by') {
        this.emit('collection_sort_change', { sort_state: field.value });
        return;
      }

      if (field.closest('#main-collection-filters')) {
        this.emit('collection_filter_apply', {
          filter: field.name || '',
          value: field.value || '',
          checked: 'checked' in field ? Boolean(field.checked) : undefined,
        });
      }
    }

    handleKeydown(event) {
      const activeTab = event.target.closest?.('[data-cs-observatory-tab]');
      if (!activeTab) return;
      const module = activeTab.closest('[data-cs-observatory]');
      const tabs = Array.from(module?.querySelectorAll('[data-cs-observatory-tab]') || []);
      const currentIndex = tabs.indexOf(activeTab);
      if (currentIndex < 0) return;

      let nextIndex;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % tabs.length;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = tabs.length - 1;
      if (nextIndex === undefined) return;

      event.preventDefault();
      this.activateObservatoryTab(tabs[nextIndex], true);
    }

    handleClick(event) {
      if (this.heroVideoBlocked) {
        this.heroVideoBlocked = false;
        this.syncHeroVideoPlayback();
      }
      const discoveryControl = event.target.closest?.('[data-cs-discovery-previous], [data-cs-discovery-next]');
      if (discoveryControl && this.root.contains(discoveryControl) && this.discoveryList) {
        event.preventDefault();
        const direction = discoveryControl.hasAttribute('data-cs-discovery-previous') ? -1 : 1;
        const distance = Math.max(220, Math.min(640, this.discoveryList.clientWidth * .78));
        this.discoveryList.scrollBy({
          left: direction * distance,
          behavior: this.heroMotionPreference?.matches ? 'auto' : 'smooth',
        });
        return;
      }

      const observatoryTab = event.target.closest?.('[data-cs-observatory-tab]');
      if (observatoryTab && this.root.contains(observatoryTab)) {
        event.preventDefault();
        this.activateObservatoryTab(observatoryTab, false);
        return;
      }

      const filterOpen = event.target.closest?.('.mobile-facets__open-wrapper, .facets__summary');
      if (filterOpen && this.root.contains(filterOpen)) {
        const label = filterOpen.querySelector('.mobile-facets__open-label, .facets__summary-label')?.textContent?.trim();
        this.emit('collection_filter_open', { filter: label || 'all' });
      }

      const link = event.target.closest?.('a');
      if (!link || !this.root.contains(link)) return;

      const loadMore = link.closest('[data-cs-load-more]');
      if (loadMore) {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.loadMore(loadMore);
        return;
      }

      const productItem = link.closest('[data-cs-product-position]');
      if (productItem) {
        this.saveScroll();
        const card = productItem.querySelector('[data-cs-product-card]');
        this.emit('collection_product_select', {
          productId: card?.dataset.csProductId || card?.dataset.productId,
          productTitle: card?.dataset.csProductTitle || card?.dataset.productTitle || card?.querySelector('.cs-product-card__title')?.textContent?.trim(),
          productHandle: card?.dataset.csProductHandle || card?.dataset.productHandle,
          variantId: card?.dataset.csVariantId || card?.dataset.variantId,
          position: Number(productItem.dataset.csProductPosition || 0),
          destination: link.pathname,
        });
        return;
      }

      if (link.closest('.cs-collection__discovery')) {
        this.emit('collection_discovery_select', {
          filter_value: link.dataset.csDiscoveryValue || '',
          destination: link.href,
        });
        return;
      }

      if (link.closest('.cs-collection__tokens')) {
        this.emit('collection_filter_remove', { destination: link.href });
      }
    }

    activateObservatoryTab(tab, moveFocus) {
      const module = tab.closest('[data-cs-observatory]');
      if (!module) return;
      const tabs = Array.from(module.querySelectorAll('[data-cs-observatory-tab]'));
      const panels = Array.from(module.querySelectorAll('[data-cs-observatory-panel]'));
      const nextIndex = tabs.indexOf(tab);
      if (nextIndex < 0 || !panels[nextIndex]) return;

      tabs.forEach((item, index) => {
        const selected = index === nextIndex;
        item.classList.toggle('is-active', selected);
        item.setAttribute('aria-selected', String(selected));
        item.tabIndex = selected ? 0 : -1;
      });
      panels.forEach((panel, index) => {
        const selected = index === nextIndex;
        panel.classList.toggle('is-active', selected);
        panel.hidden = !selected;
      });

      if (moveFocus) tab.focus();
      this.emit('observatory_product_select', {
        module_id: module.dataset.csModuleId || '',
        product_id: tab.dataset.productId || '',
        position: Number(module.dataset.csPosition || 0),
        media_type: 'poster',
      });
    }

    async loadMore(link) {
      if (link.getAttribute('aria-busy') === 'true') return;

      const destination = new URL(link.href, window.location.href);
      const requestUrl = new URL(destination.href);
      requestUrl.searchParams.set('section_id', this.sectionId);
      link.setAttribute('aria-busy', 'true');
      link.setAttribute('aria-disabled', 'true');

      try {
        const response = await fetch(requestUrl.href, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin',
        });
        if (!response.ok) throw new Error(`Collection page request failed: ${response.status}`);

        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const incomingGrid = parsed.querySelector('#product-grid');
        const currentGrid = this.root.querySelector('#product-grid');
        if (!incomingGrid || !currentGrid) throw new Error('Collection page response is missing its product grid.');

        const incomingItems = Array.from(incomingGrid.children);
        incomingItems.forEach((item) => currentGrid.append(item));
        window.CorsailleSaved?.sync?.();

        const incomingNext = parsed.querySelector('[data-cs-load-more]');
        const incomingStatus = parsed.querySelector('[data-cs-page-status]');
        const currentStatus = this.root.querySelector('[data-cs-page-status]');
        if (incomingStatus && currentStatus) currentStatus.textContent = incomingStatus.textContent;

        const visibleCount = currentGrid.querySelectorAll('[data-cs-product-position]').length;
        const countStatus = this.root.querySelector(`#CsProductCount-${CSS.escape(this.sectionId)}`);
        if (countStatus) countStatus.textContent = `${visibleCount} creații afișate`;

        if (incomingNext) {
          const nextDestination = new URL(incomingNext.getAttribute('href') || '', destination.href);
          nextDestination.searchParams.delete('section_id');
          link.href = nextDestination.href;
          link.removeAttribute('aria-busy');
          link.removeAttribute('aria-disabled');
        } else {
          link.remove();
        }

        history.replaceState(
          {
            ...(history.state || {}),
            searchParams: destination.searchParams.toString(),
            csCollectionPage: destination.searchParams.get('page') || '1',
          },
          '',
          destination.href,
        );

        this.emit('collection_load_more', {
          page: Number(destination.searchParams.get('page') || 1),
          appended_count: incomingItems.filter((item) => item.hasAttribute('data-cs-product-position')).length,
        });

        const firstProduct = incomingItems.find((item) => item.hasAttribute('data-cs-product-position'));
        firstProduct?.querySelector('[data-cs-product-link]')?.focus({ preventScroll: true });
      } catch (_error) {
        const status = this.root.querySelector('[data-cs-page-status]');
        if (status) status.textContent = 'Nu am putut încărca următoarea pagină. Continuăm prin navigare sigură.';
        link.removeAttribute('aria-busy');
        link.removeAttribute('aria-disabled');
        window.setTimeout(() => window.location.assign(destination.href), 500);
      }
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-cs-collection]').forEach((root) => {
      if (initialized.has(root)) return;
      initialized.add(root);
      controllers.set(root, new CorsailleCollectionController(root));
    });
  };

  document.addEventListener('click', (event) => {
    const link = event.target.closest?.('[data-cs-load-more]');
    if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const root = link.closest('[data-cs-collection]');
    const controller = root ? controllers.get(root) : null;
    if (!controller) return;
    event.preventDefault();
    controller.loadMore(link);
  }, { capture: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const roots = [];
    if (event.target.matches?.('[data-cs-collection]')) roots.push(event.target);
    event.target.querySelectorAll?.('[data-cs-collection]').forEach((root) => roots.push(root));
    roots.forEach((root) => controllers.get(root)?.destroy());
  });
})();
